import { useQuery } from '@tanstack/react-query';
import { CircleHelp, Inbox, OctagonAlert, Timer, type LucideIcon } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, qs } from '../../api/client';
import { useProject } from '../../api/hooks';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { LinkButton } from '../../ui/Button';
import { Alert, QueryView } from '../../ui/feedback';
import { Select } from '../../ui/forms';
import { Card, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp, shortAge } from '../../ui/Timestamp';

interface AttentionRow {
  key: string;
  title: string;
  reason: string;
  since: string;
  href: string;
}
interface Dash {
  project: { key: string; name: string; description: string; archived: boolean };
  window: { days: number; from: string; to: string };
  filters: { type: string[]; priority: string[] };
  totals: Record<
    'pending' | 'todo' | 'in_progress' | 'pending_release' | 'closed_done' | 'closed_rejected' | 'open',
    number
  >;
  window_counts: { created: number; delivered: number; rejected: number };
  blocked: number;
  staleOwners: number;
  pendingApprovalRequests: number;
  releasesAwaitingApproval: number;
  oldestReadySince: string | null;
  attention: {
    pendingAcceptance: AttentionRow[];
    blockedStale: AttentionRow[];
    ready: AttentionRow[];
    deliveries: AttentionRow[];
  };
}

/** PRJ-03: filtered aggregates with the applied filters and date window shown. */
export function DashboardPage() {
  const { key } = useParams();
  const project = useProject(key);
  const [params, setParams] = useSearchParams();
  usePageTitle(project.data ? `${project.data.name} · Dashboard` : null);
  useCrumbs([{ label: 'Projects', to: '/projects' }, { label: project.data?.name ?? key ?? '' }]);
  const window = params.get('window') ?? '30';
  const type = params.get('type') ?? '';
  const q = useQuery({
    queryKey: ['dashboard', key, window, type],
    queryFn: () => api.get<Dash>(`/projects/${key}/dashboard${qs({ window, type })}`),
  });
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };
  return (
    <div className="page">
      <QueryView query={q}>
        {(d) => (
          <>
            <PageHeader
              title={d.project.name}
              subtitle={d.project.description || undefined}
              actions={
                <>
                  {d.project.archived && <Badge>Archived</Badge>}
                  <LinkButton to={`/projects/${key}/board`}>Open board</LinkButton>
                  {!d.project.archived && (
                    <LinkButton to={`/projects/${key}/tickets/new`} variant="primary">
                      Create ticket
                    </LinkButton>
                  )}
                </>
              }
            />
            <SetupHint projectKey={key!} />
            <div className="row between">
              <span className="small muted">
                Window: last {d.window.days} days (<Timestamp value={d.window.from} dateOnly /> –{' '}
                <Timestamp value={d.window.to} dateOnly />) · Type:{' '}
                {d.filters.type.length
                  ? d.filters.type
                      .join(', ')
                      .replace('feature_request', 'feature requests')
                      .replace('issue', 'issues')
                  : 'all'}
              </span>
              <div className="filter-bar">
                <Select
                  aria-label="Date window"
                  value={window}
                  onChange={(e) => set('window', e.target.value)}
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="365">Last year</option>
                </Select>
                <Select aria-label="Type" value={type} onChange={(e) => set('type', e.target.value)}>
                  <option value="">All types</option>
                  <option value="issue">Issues</option>
                  <option value="feature_request">Feature requests</option>
                </Select>
              </div>
            </div>
            <div className="grid-3" style={{ gridTemplateColumns: 'repeat(5, minmax(0,1fr))' }}>
              <Stat n={d.totals.open} label="Open tickets" to={`/projects/${key}/board`} />
              <Stat
                n={d.totals.pending}
                label="Pending acceptance"
                to={`/projects/${key}/search?status=pending`}
              />
              <Stat
                n={d.totals.in_progress}
                label="In progress"
                to={`/projects/${key}/search?status=in_progress`}
              />
              <Stat n={d.totals.pending_release} label="Pending release" to={`/projects/${key}/releases`} />
              <Stat
                n={d.window_counts.delivered}
                label="Delivered in window"
                to={`/projects/${key}/search?status=closed&resolution=done`}
              />
            </div>
            <div className="split">
              <div className="grid-2">
                <Attention
                  title="Pending acceptance"
                  icon={Inbox}
                  count={d.totals.pending}
                  rows={d.attention.pendingAcceptance}
                  extra={
                    d.pendingApprovalRequests
                      ? `${d.pendingApprovalRequests} approval request(s) waiting`
                      : undefined
                  }
                />
                <Attention
                  title="Blocked and stale"
                  icon={OctagonAlert}
                  color="var(--color-error)"
                  count={d.blocked + d.staleOwners}
                  rows={d.attention.blockedStale}
                />
                <Attention
                  title="Failed or unknown delivery"
                  icon={CircleHelp}
                  color="#785b0e"
                  count={d.attention.deliveries.length}
                  rows={d.attention.deliveries}
                />
                <Attention
                  title="Readiness and release age"
                  icon={Timer}
                  count={d.totals.pending_release}
                  rows={d.attention.ready}
                  extra={d.oldestReadySince ? `Oldest ready for ${shortAge(d.oldestReadySince)}` : undefined}
                />
              </div>
              <Card title="Status totals" tight>
                <dl className="kv" style={{ gridTemplateColumns: '1fr auto' }}>
                  {(
                    [
                      ['Pending', d.totals.pending],
                      ['Todo', d.totals.todo],
                      ['In progress', d.totals.in_progress],
                      ['Pending release', d.totals.pending_release],
                      ['Closed · Done', d.totals.closed_done],
                      ['Closed · Rejected', d.totals.closed_rejected],
                    ] as const
                  ).map(([k, v]) => (
                    <div key={k} style={{ display: 'contents' }}>
                      <dt>{k}</dt>
                      <dd className="mono" style={{ textAlign: 'right' }}>
                        {v}
                      </dd>
                    </div>
                  ))}
                </dl>
                <span className="small muted">
                  In window: {d.window_counts.created} created · {d.window_counts.delivered} delivered ·{' '}
                  {d.window_counts.rejected} rejected (not counted as delivered)
                </span>
                {d.releasesAwaitingApproval > 0 && (
                  <Link to={`/projects/${key}/releases`}>
                    {d.releasesAwaitingApproval} release(s) awaiting approval or start
                  </Link>
                )}
              </Card>
            </div>
          </>
        )}
      </QueryView>
    </div>
  );
}

function SetupHint({ projectKey }: { projectKey: string }) {
  const p = useProject(projectKey);
  if (!p.data) return null;
  const s = p.data.setup;
  const missing = [
    !s.repository && 'repository',
    s.activeBuilders === 0 && 'builders',
    !s.hasProductionEnvironment && 'production environment',
    s.context.some((c) => c.missing.length) && 'context',
  ].filter(Boolean);
  if (!missing.length) return null;
  return (
    <Alert tone="info" title="Setup incomplete">
      <span>
        Missing: {missing.join(', ')}. <Link to={`/projects/${projectKey}/settings`}>Review setup</Link>
      </span>
    </Alert>
  );
}

function Stat({ n, label, to }: { n: number; label: string; to: string }) {
  return (
    <Link to={to} className="stat">
      <span className="n">{n}</span>
      <span className="label">{label}</span>
    </Link>
  );
}

function Attention({
  title,
  icon: Icon,
  color,
  count,
  rows,
  extra,
}: {
  title: string;
  icon: LucideIcon;
  color?: string;
  count: number;
  rows: AttentionRow[];
  extra?: string | undefined;
}) {
  return (
    <Card
      tight
      title={
        <span className="row nowrap" style={{ gap: 8 }}>
          <Icon size={18} color={color ?? 'var(--color-body)'} aria-hidden /> {title}
        </span>
      }
      action={<span className="mono muted">{count}</span>}
    >
      {rows.length === 0 ? (
        <span className="small muted">Nothing here.</span>
      ) : (
        <ul className="checklist">
          {rows.map((r) => (
            <li key={r.key} style={{ justifyContent: 'space-between' }}>
              <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                <Link to={r.href}>
                  <span className="mono">{r.key}</span> {r.title}
                </Link>
                <span className="small muted">{r.reason}</span>
              </span>
              <span className="small muted">{shortAge(r.since)}</span>
            </li>
          ))}
        </ul>
      )}
      {extra && <span className="small muted">{extra}</span>}
    </Card>
  );
}
